import {
  AAC_CODEC,
  H264_LADDER,
  H264_PROFILE_NAMES,
  type AudioTrackMode,
  type CapabilityReport,
  type RendererKind,
} from './types';

/**
 * Session-start capability probe (implementation-plan.md §2.5) — executes the plan's probe
 * verbatim in spirit and in order:
 *
 *   1. `VideoEncoder` in window?  No → `webcodecs: 'none'`, renderer `server`.
 *   2. Walk the H.264 ladder High → Main → Baseline with `VideoEncoder.isConfigSupported`.
 *      Nothing supported → `'none'`, renderer `server` (the server ffmpeg fallback takes over).
 *   3. `AudioEncoder.isConfigSupported('mp4a.40.2')`. Missing (Safari 16.4-18.7, some Linux
 *      builds) → `'partial'` + a warning: clips are video-only or audio is passed through.
 *
 * The result is the single input to adapter selection (§2.3) and is persisted on
 * TaggingSession.capability_report — never assume a device can render.
 */

/** Narrow, dependency-free view of the WebCodecs statics we probe. */
interface ConfigProbe {
  isConfigSupported(config: unknown): Promise<{ supported?: boolean }>;
}

type ProbeGlobal = typeof globalThis & {
  VideoEncoder?: ConfigProbe;
  AudioEncoder?: ConfigProbe;
};

export async function probeCapabilities(): Promise<CapabilityReport> {
  const scope = globalThis as ProbeGlobal;
  const warnings: string[] = [];

  // 1. WebCodecs at all? (Also requires a secure context: https or 127.0.0.1/localhost.)
  if (!scope.VideoEncoder || typeof scope.VideoEncoder.isConfigSupported !== 'function') {
    return {
      webcodecs: 'none',
      videoEncode: [],
      audioEncode: [],
      renderer: 'server',
      warnings: [
        scope.isSecureContext === false
          ? 'WebCodecs requires a secure context (https or localhost) — WebCodecs not available'
          : 'WebCodecs not available',
      ],
    };
  }

  // 2. H.264 ladder, best profile first. The first supported entry is the one we encode with.
  const videoEncode: string[] = [];
  for (const codec of H264_LADDER) {
    if (await isVideoConfigSupported(scope.VideoEncoder, codec)) videoEncode.push(codec);
  }

  if (videoEncode.length === 0) {
    return {
      webcodecs: 'none',
      videoEncode: [],
      audioEncode: [],
      renderer: 'server',
      warnings: ['No H.264 encoder available — rendering on the server'],
    };
  }

  // 3. Audio. `AudioEncoder` may not exist at all (Safari 16.4-18.7) or may reject AAC.
  const audioEncode: string[] = [];
  if (scope.AudioEncoder && typeof scope.AudioEncoder.isConfigSupported === 'function') {
    if (await isAudioConfigSupported(scope.AudioEncoder)) audioEncode.push('aac');
  }

  if (audioEncode.length === 0) {
    warnings.push('No AAC encoder — clips fall back to audio passthrough or video-only');
  }

  const kind = videoEncode[0];
  warnings.push(`H.264 ${H264_PROFILE_NAMES[kind] ?? kind} (${kind}) selected`);

  return {
    webcodecs: audioEncode.length > 0 ? 'full' : 'partial',
    videoEncode,
    audioEncode,
    renderer: 'webcodecs',
    warnings,
  };
}

async function isVideoConfigSupported(encoder: ConfigProbe, codec: string): Promise<boolean> {
  try {
    // 1280x720 is the ladder's probe size (plan §2.5) — the clip resolution is chosen later.
    const res = await encoder.isConfigSupported({
      codec,
      width: 1280,
      height: 720,
      bitrate: 2_000_000,
      framerate: 30,
    });
    return res?.supported === true;
  } catch {
    return false;
  }
}

async function isAudioConfigSupported(encoder: ConfigProbe): Promise<boolean> {
  try {
    const res = await encoder.isConfigSupported({
      codec: AAC_CODEC,
      sampleRate: 48_000,
      numberOfChannels: 2,
      bitrate: 128_000,
    });
    return res?.supported === true;
  } catch {
    return false;
  }
}

/** §2.3 adapter selection: WebCodecs when the ladder found an H.264 encoder, else the server. */
export function rendererFor(report: CapabilityReport): RendererKind {
  return report.renderer;
}

/**
 * What happens to a clip's audio, given the report and the caller's preference (§2.6).
 *
 * - an explicit request wins (the UI may force `mute`, or force `passthrough` on Safari);
 * - `reencode` needs an AAC encoder;
 * - `passthrough` needs the source track to already be AAC — anything else is a silent
 *   re-encode we cannot do, so it degrades to video-only rather than shipping broken audio.
 */
export function resolveAudioMode(
  report: CapabilityReport,
  requested: AudioTrackMode | undefined,
  sourceAudioCodec: string | null,
): AudioTrackMode {
  if (requested === 'mute') return 'mute';
  const canAac = report.audioEncode.includes('aac');
  if (requested === 'reencode') return canAac ? 'reencode' : 'mute';
  if (requested === 'passthrough') {
    return sourceAudioCodec === 'aac' ? 'passthrough' : 'mute';
  }
  if (canAac) return 'reencode';
  return sourceAudioCodec === 'aac' ? 'passthrough' : 'mute';
}
